import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faDatabase,
  faFilePowerpoint,
  faChartBar,
  faBars,
  faTimes,
  faArrowRight,
  faFileUpload,
  faEnvelopeOpenText,
  faImages,
  faMagicWandSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import styles from './Home.module.css';

// ─── Navigation ────────────────────────────────────────────────────────────────

function Navigation() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`${styles.navbar} ${isScrolled ? styles.navbarScrolled : ''}`}>
      <div className={styles.navContainer}>
        <div className={styles.navInner}>
          <button onClick={() => navigate('/')} className={styles.navLogo}>
            <img src="/logobig.png" alt="Ebombo" className={styles.navLogoImage} />
          </button>

          <div className={styles.navLinksDesktop}>
            <a href="#features" className={styles.navLink}>
              Features
            </a>
            <a href="#how-it-works" className={styles.navLink}>
              How It Works
            </a>
          </div>

          <div className={styles.navActionsDesktop}>
            <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.login)}>
              Sign in
            </Button>
          </div>

          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={styles.mobileMenuBtn}
            aria-label="Toggle menu"
          >
            <FontAwesomeIcon icon={isMobileOpen ? faTimes : faBars} />
          </button>
        </div>

        {isMobileOpen && (
          <div className={styles.mobileMenu}>
            <a
              href="#features"
              className={styles.mobileNavLink}
              onClick={() => setIsMobileOpen(false)}
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className={styles.mobileNavLink}
              onClick={() => setIsMobileOpen(false)}
            >
              How It Works
            </a>
            <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.login)}>
              Sign in
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
}

// ─── Hero Section ──────────────────────────────────────────────────────────────

function HeroSection() {
  const navigate = useNavigate();
  return (
    <section className={styles.heroSection}>
      <div className={styles.heroContainer}>
        <div className={styles.heroText}>
          <p className={styles.heroEyebrow}>Corporate Event Management</p>
          <h1 className={styles.heroTitle}>
            The CRM
            <br />
            for event planners.
          </h1>
          <p className={styles.heroSubtitle}>
            Manage providers, generate AI-powered quotations, and produce event presentations — all
            in one internal workspace.
          </p>
          <div className={styles.heroActions}>
            <Button variant="primary" size="lg" onClick={() => navigate(ROUTES.login)}>
              Sign in to your workspace
            </Button>
          </div>
        </div>

        <div className={styles.heroVisual}>
          <div className={styles.screenFrame}>
            <div className={styles.screenBar}>
              <div className={styles.screenDots}>
                <span className={`${styles.dot} ${styles.dotRed}`} />
                <span className={`${styles.dot} ${styles.dotYellow}`} />
                <span className={`${styles.dot} ${styles.dotGreen}`} />
              </div>
              <div className={styles.screenUrl}>suppliers.ebombo.com</div>
            </div>
            <img src="/screen_client.jpg" alt="Ebombo dashboard" className={styles.screenImg} />
          </div>
          <div className={styles.screenFrameAccent}>
            <div className={styles.screenBar}>
              <div className={styles.screenDots}>
                <span className={`${styles.dot} ${styles.dotRed}`} />
                <span className={`${styles.dot} ${styles.dotYellow}`} />
                <span className={`${styles.dot} ${styles.dotGreen}`} />
              </div>
              <div className={styles.screenUrl}>suppliers.ebombo.com</div>
            </div>
            <img src="/screen_provider.jpg" alt="Provider catalog" className={styles.screenImg} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Features Section ──────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: faComments,
    title: 'AI Quotation Chat',
    description:
      'Describe your event in plain language. The AI searches your provider database, scores fit, and returns a structured quotation with line items and adjustable margins — in seconds.',
  },
  {
    icon: faDatabase,
    title: 'Provider Management',
    description:
      'Maintain a curated database of vetted providers. Upload documents and let AI extract services, capacity, pricing, and specs automatically.',
  },
  {
    icon: faFilePowerpoint,
    title: 'Presentation Generator',
    description:
      'Generate polished event decks and invitations from a wizard. AI writes the content; Gemini generates per-slide images. Export to PPTX or edit in Google Slides.',
  },
  {
    icon: faChartBar,
    title: 'Cost Tracking',
    description:
      'Every AI call is logged with token counts and costs. Know exactly how much each quotation or presentation generation costs across Claude and Gemini.',
  },
];

function FeaturesSection() {
  return (
    <section id="features" className={styles.featuresSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Everything your team needs.</h2>
          <p className={styles.sectionSubtitle}>
            Four core capabilities, built tight around the corporate event workflow.
          </p>
        </div>
        <div className={styles.featuresGrid}>
          {FEATURES.map((f) => (
            <div key={f.title} className={styles.featureCard}>
              <div className={styles.featureIconWrap}>
                <FontAwesomeIcon icon={f.icon} className={styles.featureIcon} />
              </div>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── AI Tools Section ─────────────────────────────────────────────────────────

const AI_TOOLS = [
  {
    icon: faComments,
    name: 'Quotation Chat',
    tag: 'Chat',
    description:
      'Describe your event in natural language. The AI searches your provider database, evaluates fit across location, capacity and services, and returns a structured quotation with line items, margins and totals.',
    highlights: [
      'Natural language queries',
      'Multi-provider comparison',
      'Adjustable margins per line',
    ],
  },
  {
    icon: faFileUpload,
    name: 'Document Scanner',
    tag: 'Upload',
    description:
      'Upload provider PDFs, brochures or rate cards. AI extracts services, pricing, capacity, specs and contact info — creating structured provider records without any manual data entry.',
    highlights: ['PDF & image support', 'Auto-extracts pricing', 'Bulk document processing'],
  },
  {
    icon: faFilePowerpoint,
    name: 'Presentation Generator',
    tag: 'Generate',
    description:
      'Choose a template and theme, add your event details, and the AI writes a 50+ slide event deck with cost tables, timelines and speaker notes. Export to PPTX or push to Google Slides.',
    highlights: ['20 plantilla templates', '14 event themes', 'Google Slides integration'],
  },
  {
    icon: faEnvelopeOpenText,
    name: 'Invitation Generator',
    tag: 'Generate',
    description:
      'Create branded event invitations with AI-written copy and Gemini-generated imagery. Configure event details, pick a visual theme, and generate a polished invitation deck in seconds.',
    highlights: ['AI-written copy', 'Per-slide image generation', 'Download as PPTX'],
  },
  {
    icon: faImages,
    name: 'Render Generator',
    tag: 'Generate',
    description:
      'Produce photorealistic venue renders for client pitches. Describe the desired ambiance, select a theme, and Gemini generates multiple high-resolution venue visualizations.',
    highlights: ['Photorealistic output', 'Multiple variations', 'Theme-based styling'],
  },
];

function AIToolsSection() {
  return (
    <section className={styles.toolsSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <p className={styles.toolsEyebrow}>
            <FontAwesomeIcon icon={faMagicWandSparkles} className={styles.toolsEyebrowIcon} />
            AI-Powered Tools
          </p>
          <h2 className={styles.sectionTitle}>Five tools. One workflow.</h2>
          <p className={styles.sectionSubtitle}>
            Every tool is built around the same provider database — so quotations, presentations and
            invitations always use your real data.
          </p>
        </div>

        <div className={styles.toolsList}>
          {AI_TOOLS.map((tool, idx) => (
            <div key={tool.name} className={styles.toolItem}>
              <div className={styles.toolIndex}>
                <span className={styles.toolIndexNum}>{String(idx + 1).padStart(2, '0')}</span>
                <span className={styles.toolTag}>{tool.tag}</span>
              </div>
              <div className={styles.toolContent}>
                <div className={styles.toolHeader}>
                  <div className={styles.toolIconWrap}>
                    <FontAwesomeIcon icon={tool.icon} className={styles.toolIcon} />
                  </div>
                  <h3 className={styles.toolName}>{tool.name}</h3>
                </div>
                <p className={styles.toolDesc}>{tool.description}</p>
                <div className={styles.toolHighlights}>
                  {tool.highlights.map((h) => (
                    <span key={h} className={styles.toolChip}>
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── How It Works ──────────────────────────────────────────────────────────────

const STEPS = [
  {
    num: '01',
    title: 'Staff adds providers',
    description:
      'Admins upload provider documents. AI scans each file and extracts services, pricing, and specs into structured records — no manual data entry.',
  },
  {
    num: '02',
    title: 'Chat to get a quotation',
    description:
      'Event managers describe the brief in chat. The AI searches active providers, scores fit, and returns a detailed quotation with margins ready to adjust.',
  },
  {
    num: '03',
    title: 'Generate the presentation',
    description:
      'With one click, produce a professional event deck or invitation. AI writes every slide; export to PPTX or push to Google Slides for final edits.',
  },
];

function HowItWorksSection() {
  return (
    <section id="how-it-works" className={styles.howSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>How it works.</h2>
          <p className={styles.sectionSubtitle}>
            Three steps from a blank brief to a client-ready presentation.
          </p>
        </div>
        <div className={styles.stepsGrid}>
          {STEPS.map((step, idx) => (
            <div key={step.num} className={styles.stepCard}>
              <div className={styles.stepNum}>{step.num}</div>
              {idx < STEPS.length - 1 && (
                <div className={styles.stepConnector} aria-hidden="true" />
              )}
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDesc}>{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ─────────────────────────────────────────────────────────────────

function FinalCTA() {
  const navigate = useNavigate();
  return (
    <section className={styles.ctaSection}>
      <div className={styles.ctaInner}>
        <h2 className={styles.ctaTitle}>Ready to get started?</h2>
        <p className={styles.ctaSubtitle}>
          Sign in with your invited account and start managing events.
        </p>
        <Button variant="primary" size="lg" onClick={() => navigate(ROUTES.login)}>
          Sign in
          <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '10px' }} />
        </Button>
        <p className={styles.ctaNote}>Contact your administrator for access.</p>
      </div>
    </section>
  );
}

// ─── Footer ────────────────────────────────────────────────────────────────────

function Footer() {
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <button onClick={() => navigate('/')} className={styles.footerLogo}>
          <img src="/logobig.png" alt="Ebombo" className={styles.footerLogoImg} />
        </button>
        <p className={styles.footerCopy}>Built by Ebombo &mdash; {year}</p>
        <div className={styles.footerLinks}>
          <a href="https://suppliers.ebombo.com/privacy" className={styles.footerLink}>
            Privacy Policy
          </a>
          <span className={styles.footerLinkSep}>&middot;</span>
          <a href="https://suppliers.ebombo.com/terms" className={styles.footerLink}>
            Terms of Service
          </a>
        </div>
      </div>
    </footer>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function Home() {
  return (
    <main className={styles.main}>
      <Navigation />
      <HeroSection />
      <FeaturesSection />
      <AIToolsSection />
      <HowItWorksSection />
      <FinalCTA />
      <Footer />
    </main>
  );
}
